import moment from "moment";
import React, { Dispatch as ReactDispatch, SetStateAction } from "react";
import { connect, ConnectedProps } from "react-redux";
import { IStore } from "redux/store";
import { TCNRColumn, TCNRColumnsIDs } from "redux/app/vessel/onboard/types";
import classNames from "classnames";
import { getTimelineRange } from "redux/app/vessel/onboard/utils";
import { Dispatch } from "redux";
import { logActions } from "redux/app/logs";
import StickyColumns from "../_elements/StickyColumns";

interface IReportRowProps {
	report: any;
	key: number;
	onClick: (report: any) => void;
	indexNumber: number;
	selectedColumns: TCNRColumn[];
	isSignalStatusEnabled?: boolean;
	isIMOSStatusEnabled?: boolean;
	isDocStatusEnabled?: boolean;
	setStatusModalOpen: ReactDispatch<SetStateAction<boolean>>;
	setErrorDetails: ReactDispatch<SetStateAction<any>>;
	setIsToolsModalOpen: ReactDispatch<SetStateAction<boolean>>;
	setRemarksModal: ReactDispatch<SetStateAction<string>>;
	stickyOffsets: Record<string, { sm: number; lg: number }>;
}

const ReportRow: React.FC<IReportRowProps & PropsFromRedux> = (props) => {
	const {
		report,
		selectedColumns,
		isSignalStatusEnabled,
		isIMOSStatusEnabled,
		isDocStatusEnabled,
		setStatusModalOpen,
		setErrorDetails,
		setIsToolsModalOpen,
		setRemarksModal,
		stickyOffsets,
	} = props;

	// Check if any timeline items are invalid
	// Note: Embedded cargo reports (part of departure reports) don't have isValid flag and should be excluded from validation
	const hasInvalidItems = report?.timeline?.some((item: any) => {
		// Skip validation for embedded cargo reports
		if (item?.report_type === "DEPARTURE" && item?.cargoReport && Array.isArray(item?.cargoReport)) {
			// This is a departure report with embedded cargo reports, check only the departure report itself
			return item?.isValid === false;
		}
		return item?.isValid === false;
	});

	const hasUnsetItems = report?.timeline?.some((item: any) => {
		// Skip validation for embedded cargo reports
		if (item?.report_type === "DEPARTURE" && item?.cargoReport && Array.isArray(item?.cargoReport)) {
			// This is a departure report with embedded cargo reports, check only the departure report itself
			return item?.isValid === undefined || item?.isValid === null;
		}
		return item?.isValid === undefined || item?.isValid === null;
	});

	// Determine row styling based on validation status
	const getRowClassName = () => {
		const baseClass = "my-1 border hover:cursor-pointer";
		if (hasInvalidItems) {
			return `${baseClass} bg-red-50 hover:bg-red-100`;
		} else if (hasUnsetItems) {
			return `${baseClass} bg-yellow-50 hover:bg-yellow-100`;
		} else {
			return `${baseClass} hover:bg-gray-200`;
		}
	};
	const totalSubRows = report?.timeline?.length || 1;
	const { startingDate, endingDate } = getTimelineRange(report?.timeline);

	const ignoredColumns = [
		"from_utc_date",
		"report_type",
		"createdAt",
	]

	const getConditionClassnames = (condition: string) => {
		switch (condition) {
			case "Port":
				return "text-blue-500 bg-blue-100 px-1 py-0.5 border border-blue-500 rounded-full"
			case "Laden":
				return "text-yellow-500 bg-yellow-100 px-1 py-0.5 border border-yellow-500 rounded-full"
			case "Ballast":
				return "text-green-500 bg-green-100 px-1 py-0.5 border border-green-500 rounded-full"
			default:
				return "text-gray-500 bg-gray-100 px-1 py-0.5 border border-gray-500 rounded-full"
		}
	}
	return (
		<>
			{report?.timeline?.map((reportItem: any, index: number) => {
				const reportType = reportItem?.report_type as keyof TCNRColumnsIDs;
				const isFirstRow = index === 0;
				return (
					<tr
						key={report._id + reportType + reportItem?.from_utc_date + index}
						className={classNames(
							"text-center border px-0.5 py-0.5 md:px-2 md:py-4 text-[9px] md:text-xs lg:text-sm max-h-16",
							getRowClassName(),
							reportType === "ARRIVAL" || reportType === "DEPARTURE" ? "bg-blue-100" : "bg-white"
						)}
					>
						{isFirstRow && (
							<td rowSpan={totalSubRows} className="border-r text-center text-gray-500 px-0.5 py-0.5 md:px-2 md:py-1 text-[9px] md:text-xs lg:text-sm">
								{props.indexNumber}
							</td>
						)}
						{isFirstRow && selectedColumns?.find(item => item?.ids?.STEAMING === "from_utc_date") ?
							<td
								className="text-center text-gray-700 px-0.5 py-0.5 md:px-2 md:py-1 text-[6px] min-w-64 md:text-xs lg:text-sm"
								rowSpan={totalSubRows}
							>
								{(startingDate && endingDate) ?
									<div className="flex gap-2 justify-around">
										<div className="flex-col flex">
											{moment(startingDate.split("T")[0]).format("DD MMM YYYY")}
											<div className="flex gap-2">
												<span className="text-gray-500">{startingDate.split("T")[1]}</span>
											</div>
										</div>
										<span className="text-gray-500"> - </span>
										<div className="flex-col flex">
											{moment(endingDate.split("T")[0]).format("DD MMM YYYY")}
											<div className="flex gap-2">
												<span className="text-gray-500">{endingDate.split("T")[1]}</span>
											</div>
										</div>
									</div>

									: "-"
								}
							</td>
							: null}
						{isFirstRow && selectedColumns?.find(item => item?.ids?.STEAMING === "createdAt") ?
							<td
								className="border-r font-medium text-center text-gray-700 px-0.5 py-0.5 md:px-2 md:py-1 text-[6px] md:text-xs lg:text-sm"
								rowSpan={totalSubRows}
								title={moment(report.createdAt).format("DD-MM-YYYY hh:mm a")}
							>
								<p>{moment.utc(report?.createdAt).format("DD-MM-YYYY")}</p>
								<p>{moment.utc(report?.createdAt).format("hh:mm a")}</p>
							</td>
							: null}
						{selectedColumns?.find(item => item?.ids?.STEAMING === "report_type") ?
							<td>
								<span className="border rounded-lg shadow px-1 py-0.5 bg-white">
									{reportType}
								</span>
							</td>
							: null}
						{selectedColumns?.filter(item => !ignoredColumns?.includes(item?.ids?.[reportType] || ""))?.map(column => {
							if (column?.children) {
								return column.children?.map((child: any) => {
									const columnId = child?.ids?.[reportType];
									return (
										<td key={columnId} className="text-center text-gray-800 px-0.5 py-0.5 md:px-2 md:py-4 text-[9px] md:text-xs lg:text-sm">
											{reportItem?.[columnId] ?? "-"}
										</td>
									)
								})
							}
							const columnId = column?.ids?.[reportType];
							return (
								<td 
									key={columnId} 
									className="text-center font-medium text-gray-800 px-0.5 py-0.5 md:px-2 md:py-4 text-[9px] md:text-xs lg:text-sm"
									onClick={() => {
										if(!columnId || !reportItem?.[columnId]) return;
										if (column.label === "Remarks") {
											setRemarksModal(reportItem?.[columnId])
										}
									}}
								>
									{columnId && reportItem?.[columnId] ?
										<div className={
											classNames(
												column.label === "Remarks" ? "w-96" : null
											)}
										>
											<span
												className={classNames(
													// Classes for special columns
													{
														"border rounded-lg shadow px-1 py-0.5 bg-white": column.label === "Status",
													},
													column.label === "Condition" ? getConditionClassnames(reportItem?.[columnId]) : null,
												)}
												title={reportItem?.[columnId]}
											>
												{column.label === "Remarks" ?
													<>
														{reportItem?.[columnId]?.slice(0, 50)}
														{reportItem?.[columnId]?.length >= 50 ?
														<>
															<span
																className="cursor-pointer text-gray-600 text-lg"
															>
																...
															</span>
														</>
															: null}
													</>
													: reportItem?.[columnId]
												}
											</span>
										</div>
										: "-"
									}
								</td>
							)
						})}
						<StickyColumns
							report={report}
							reportItem={reportItem}
							reportType={reportType}
							index={index}
							isFirstRow={isFirstRow}
							totalSubRows={totalSubRows}
							isSignalStatusEnabled={isSignalStatusEnabled}
							isIMOSStatusEnabled={isIMOSStatusEnabled}
							isDocStatusEnabled={isDocStatusEnabled}
							stickyOffsets={stickyOffsets}
							setStatusModalOpen={setStatusModalOpen}
							setErrorDetails={setErrorDetails}
							onClickView={() => {
								props.onClick(report);
							}}
							onClickTools={() => {
								props.loadReport(report._id)
								setIsToolsModalOpen(true);
							}}
						/>
					</tr >
				)
			})}
		</>
	);
}

function mapStateToProps(store: IStore, ownProps: IReportRowProps) {
	return {};
}

function mapDispatchToProps(dispatch: Dispatch) {
	return {
		loadReport(reportId: string) {
			dispatch(logActions.commands.singleReportLoad(reportId));
		},
	};
}
const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(ReportRow);